import React, { useState, useEffect } from 'react';
import { Announcement } from '@hawkins/components';
import { useTranslation } from 'react-i18next';

interface AnnouncementBannerProps {
  enabled?: boolean;
  onDismiss?: () => void;
}

export const AnnouncementBanner: React.FC<AnnouncementBannerProps> = ({
  enabled = true,
  onDismiss
}) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check screen size on mount and resize
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setIsVisible(false);
      return;
    }

    // Show the banner when component mounts (no localStorage check)
    setIsVisible(true);
  }, [enabled]);

  const handleDismiss = () => {
    // Only dismiss for current session, no localStorage persistence
    setIsVisible(false);
    onDismiss?.();
  };

  if (!isVisible || !enabled) return null;

  // Apply positioning directly to the Announcement component via a wrapper only when needed
  const wrapperStyle: React.CSSProperties = isMobile ? {
    // Mobile: centered positioning
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 1000,
  } : {
    // Desktop: bottom-end positioning (RTL-aware via CSS logical properties)
    position: 'fixed',
    bottom: '20px',
    insetInlineEnd: '20px', // Automatically flips to left for RTL
    zIndex: 1000,
  };

  const announcementClasses = {
    containerInner: 'announcement-container-custom'
  };

  return (
    <div style={wrapperStyle}>
      <style>
        {`
          .announcement-container-custom > *,
          .announcement-container-custom * {
            text-wrap: balance !important;
            line-height: 1.3 !important;
            hyphens: auto !important;
          }
          
          /* Center all text for both LTR and RTL */
          .announcement-container-custom > *,
          .announcement-container-custom * {
            text-align: center !important;
          }
        `}
      </style>
      <Announcement
        classes={announcementClasses}
        primaryActionHandler={handleDismiss}
        title={t('common.announcement.title')}
        variant="maintenance-next"
      >
        {t('common.announcement.message')}
      </Announcement>
    </div>
  );
};